/* eslint-disable @typescript-eslint/naming-convention */
import { useContext, useRef, useState, useCallback, useMemo, useEffect } from "react";

// contexts
import { ApiContext, CheckoutActionTypes, CheckoutContext, isEmpty } from "@swiggy-private/checkout-core";
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartActionTypes } from "@cart-client/reducers/CartReducer";

// helpers
import { useMountedRef, captureError, randomStringIdGenerator, isValidNumber } from "@swiggy-private/core-ui";
import {
    autoSelectChildCartAfterDelete,
    isEmptyMROCart,
    updateCartDataWithSelectedRxIds,
} from "@cart-client/helpers/MROFoodCart";
import {
    filterSelectedCartsForUpdateCartCallsFromLocalCart,
    prepareInternalWebLocalCartPayloadFromServerAndLocalCart,
} from "@cart-client/helpers/MROFoodCartPayloadPrep";
import {
    hasCartAddressChanged,
    isQuickCheckoutFlow,
    refreshQuickCheckoutParentCollection,
} from "@cart-client/helpers/QuickCheckoutCart";

// apis
import { updateCartPublic } from "@cart-client/apis/publicCart";

// interfaces
import { MROReviewedCart, MROUpdateCartData } from "@cart-client/interfaces/MROFoodCart";
import { MRONativeLocalCart } from "@swiggy-private/cart-types";

// constants
import { HTTP_STATUS_CODES } from "@cart-client/constants/index";
import { QuickCheckoutContext } from "@cart-client/contexts/QuickCheckoutContext";

interface MROUpdateCartState {
    updateCartInProgress: boolean;
    updateCartData?: MROUpdateCartData | null;
    updateCartError?: { httpStatusCode?: number };
}

type UseMROUpdateCartReturnType = [
    MROUpdateCartState,
    (data: NonNullable<MROUpdateCartState["updateCartData"]>) => Promise<MROReviewedCart | null | undefined>,
    VoidFunction,
];

/**
 * MRO (Multi-Restaurant Order) cart update hook.
 *
 * Mirrors `usePerformCartUpdate`'s public shape (`[state, doUpdateCart, reloadCart]`)
 * so `MROFoodCartActionProvider` can consume it exactly like the regular provider
 * consumes `usePerformCartUpdate`. Kept deliberately minimal — group-cart, toast
 * and prefetch concerns from the BAU hook are intentionally omitted.
 */
export const usePerformMROFoodCartUpdate = (): UseMROUpdateCartReturnType => {
    const isMountedRef = useMountedRef();
    const apiContextValue = useContext(ApiContext);
    const { setCartState, cartState } = useContext(CartContext);
    const { setRefreshParentScreenOnRemount } = useContext(QuickCheckoutContext);
    const apiCallRef = useRef(false);
    const [updateCartState, setUpdateCartState] = useState<MROUpdateCartState>({ updateCartInProgress: false });
    const apiHost = apiContextValue.host.unwrap();

    const { checkoutState } = useContext(CheckoutContext);

    // isQuickCheckoutCartFlow - is cart flow for Quick Checkout Pages
    // isQuickCheckoutMROFoodCartFlow - is cart flow for MRO Food Cart Pages, user started with Quick Checkout but currently is on Full Page Cart view for Quick Checkout
    const [isQuickCheckoutCartFlow, isQuickCheckoutMROFoodCartFlow] = useMemo(() => {
        const isQuickCheckout = isQuickCheckoutFlow();
        const qcTypes = [CheckoutActionTypes.SHOW_QUICK_CHECKOUT];
        const mroTypes = [CheckoutActionTypes.SHOW_MRO_FOOD_CART, CheckoutActionTypes.SHOW_MRO_FOOD_COUPON_LISTING];
        return [
            isQuickCheckout && qcTypes.includes(checkoutState.type),
            isQuickCheckout && mroTypes.includes(checkoutState.type),
        ];
    }, [checkoutState.type]);

    // Address the cart currently holds, kept in a ref so `doUpdateCart` always compares
    // against the committed value. The address and OFO bottom sheets are native and resolve
    // long after the click that opened them, so their callbacks hold an older render's
    // closure — a ref keeps the comparison correct without rebuilding `doUpdateCart`.
    const localCartAddressId = (cartState.localCart as MRONativeLocalCart | null)?.address_id;
    const localCartAddressIdRef = useRef(localCartAddressId);
    useEffect(() => {
        localCartAddressIdRef.current = localCartAddressId;
    }, [localCartAddressId]);

    // for Magic Item timer backend uses a cache to track if magic items should be enabled/invalidated for a user
    // for BAU food cart a cart delete/flush or a new rxid cart both invalidated the cart and magic item was removed from cache
    // for MRO food cart we have restaurant select/unselect operation + restaurant cart can deleted in case all item OOS/RX closed/user removed all items
    // we need to pass a flag - is_child_cart_delete_operation_performed to backend to tell if a child_cart was actually deleted or user just did select/unselect
    const localCartLengthRef = useRef(0);
    useEffect(() => {
        localCartLengthRef.current = (cartState.localCart as MRONativeLocalCart | null)?.child_carts?.length ?? 0;
    }, [cartState.localCart]);

    /**
     * Internal function to handle the cart update success.
     * It updates the local cart with the new cart data.
     * If the response is empty, then it clears the local cart.
     * If the response is not empty, then it updates the local cart.
     */
    const handleUpdateCartSuccess = useCallback(
        async (response: MROReviewedCart, localCart: MROUpdateCartData): Promise<void> => {
            if (!isEmpty(response)) {
                // if the response is empty, then clear the local cart
                if (isEmptyMROCart(response.data)) {
                    setCartState({
                        type: CartActionTypes.CLEAR_LOCAL_CART,
                        payload: { reviewedCart: response },
                    });
                    return;
                }

                // if the response is not empty, then update the local cart
                setCartState({
                    type: CartActionTypes.UPDATE_CART,
                    payload: {
                        uniqueIdForCart: randomStringIdGenerator(),
                        localCart: prepareInternalWebLocalCartPayloadFromServerAndLocalCart(
                            response,
                            localCart as MRONativeLocalCart,
                        ),
                        reviewedCart: response,
                    },
                });
            }
        },
        [setCartState],
    );

    /**
     * Internal function to perform the cart update.
     */
    const performCartUpdate = useCallback(
        async (updateCartData: MROUpdateCartData): Promise<MROReviewedCart | null> => {
            if (!isMountedRef.current || apiCallRef.current || !updateCartData) {
                return null;
            }

            apiCallRef.current = true;
            try {
                // A deleted child cart can leave no tab selected; auto-select one so we don't
                // send an update with zero selected carts.
                updateCartData = autoSelectChildCartAfterDelete(updateCartData);

                const filterSelectedCarts = filterSelectedCartsForUpdateCartCallsFromLocalCart(
                    updateCartData as MRONativeLocalCart,
                );
                const updateCartResponse = await updateCartPublic<MROReviewedCart>(
                    apiHost,
                    filterSelectedCarts as MRONativeLocalCart,
                );
                const updatedCartData = updateCartDataWithSelectedRxIds(updateCartData, updateCartResponse);
                await handleUpdateCartSuccess(updateCartResponse, updatedCartData);
                if (isMountedRef.current) {
                    apiCallRef.current = false;
                    setUpdateCartState({ updateCartInProgress: false });
                }
                return updateCartResponse;
            } catch (e) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const errorObj = e as { status?: number; error?: { status?: number } };
                const httpStatusCode = errorObj?.status ?? errorObj?.error?.status;
                const updateCartError =
                    httpStatusCode === HTTP_STATUS_CODES.TOO_MANY_REQUESTS ? { httpStatusCode } : undefined;
                if (isMountedRef.current) {
                    apiCallRef.current = false;
                    setUpdateCartState({ updateCartData: null, updateCartInProgress: false, updateCartError });
                }
                captureError(e);
                return null;
            }
        },
        [apiHost, handleUpdateCartSuccess, isMountedRef],
    );

    /**
     * Main function to perform the cart update.
     * It takes the updateCartData and performs the cart update.
     * Internally filtering out the unselected carts from the local cart for the update-cart API call.
     * An address change also refreshes the quick checkout parent collection, which goes stale on it.
     */
    const doUpdateCart = useCallback(
        async (updateCartData: MROUpdateCartData): Promise<MROReviewedCart | null | undefined> => {
            if (isMountedRef.current && !updateCartState.updateCartInProgress) {
                if (isQuickCheckoutMROFoodCartFlow) {
                    // Only address is checked: the parent APP collection screen renders SLA and
                    // serviceability itself, so it goes stale on a change. Every other cart field
                    // (coupon, tip, quantity) reaches quick checkout through the shared CartContext.
                    // callers spread the whole local cart, so only a value diff marks an address change
                    const isAddressUpdate = hasCartAddressChanged(
                        updateCartData?.address_id,
                        localCartAddressIdRef.current,
                    );
                    // full page cart: flag before the API call, user can go back to quick checkout mid-update
                    if (isAddressUpdate) {
                        setRefreshParentScreenOnRemount(true);
                    }
                }

                // if the child carts length is less than the local cart length, then we need to tell backend if a child_cart was actually deleted
                // this is needed for magic item timer backend to invalidate the cache for the user in case of cart delete operation
                const childCartsLength = updateCartData?.child_carts?.length ?? 0;
                if (!isEmpty(updateCartData) && isValidNumber(childCartsLength)) {
                    updateCartData.is_child_cart_delete_operation_performed = childCartsLength < localCartLengthRef.current;
                }

                setUpdateCartState({ updateCartInProgress: true, updateCartData });
                return await performCartUpdate(updateCartData);
            }
        },
        [
            isMountedRef,
            updateCartState.updateCartInProgress,
            performCartUpdate,
            isQuickCheckoutMROFoodCartFlow,
            setRefreshParentScreenOnRemount,
        ],
    );

    /**
     * reloadCart is used to reload the cart after a cart update.
     */
    const reloadCart = useCallback(async () => {
        if (isMountedRef.current && !updateCartState.updateCartInProgress) {
            // for quick checkout we don't want to reload cart as it is a partial webview on app discovery
            // we want to tell app to refresh the parent collection which will also refresh webview
            // specifically being used on login and error reload cart cases
            if (isQuickCheckoutCartFlow) {
                refreshQuickCheckoutParentCollection();
                return;
            } else if (isQuickCheckoutMROFoodCartFlow) {
                setRefreshParentScreenOnRemount(true);
            }
            setUpdateCartState({ updateCartInProgress: true, updateCartData: { ...cartState.localCart } });
            // performCartUpdate takes the hook's UpdateCartData queue shape; the BAU
            // localCart is assignable. It is re-cast to MRONativeLocalCart at the API call.
            await performCartUpdate({ ...(cartState.localCart as MRONativeLocalCart) });
        }
    }, [
        isMountedRef,
        updateCartState.updateCartInProgress,
        isQuickCheckoutCartFlow,
        isQuickCheckoutMROFoodCartFlow,
        cartState.localCart,
        performCartUpdate,
        setRefreshParentScreenOnRemount,
    ]);

    return [updateCartState, doUpdateCart, reloadCart];
};
